import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import {
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Badge,
  Box,
  AppBar,
  Toolbar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MailIcon from '@mui/icons-material/Mail';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/authUtils';
import ThemeToggle from './common/ThemeToggle';
import LanguageSwitcher from './common/LanguageSwitcher';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

const HeaderWrapper = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  padding: theme.spacing(1, 2),
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.appBar,
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.standard,
  }),
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  maxWidth: 1200,
  margin: '0 auto',
  width: '100%',
}));

const Logo = styled(Link)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.primary.contrastText,
  textDecoration: 'none',
  letterSpacing: '1px',
  transition: theme.transitions.create(['opacity'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    opacity: 0.9,
  },
  '&:focus-visible': {
    outline: '3px solid #4a90e2',
    outlineOffset: '2px',
    borderRadius: '4px',
  },
}));

const NavLinks = styled('nav')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  alignItems: 'center',
}));

const NavLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  textDecoration: 'none',
  fontWeight: theme.typography.fontWeightMedium,
  padding: theme.spacing(0.75, 1.5),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&:focus-visible': {
    outline: '3px solid #4a90e2',
    outlineOffset: '2px',
  },
}));

const UserMenu = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
}));

function Header() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [notifications, setNotifications] = useState(0);
  const [messages, setMessages] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserData();
      fetchNotifications();
    }
  }, [isAuthenticated]);

  const fetchUserData = async () => {
    try {
      const response = await api.get('/profile');
      setUserData(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Errore nel recupero dei dati utente:', error);
      }
    }
  };

  const fetchNotifications = async () => {
    try {
      const notificationResponse = await api.get('/notifications');
      const messageResponse = await api.get('/messages/unread');
      setNotifications(notificationResponse.data.unread_notifications);
      setMessages(messageResponse.data.unread_messages);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Errore nel recupero delle notifiche:', error);
      }
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget); // Apre il menu
  };

  const handleMenuClose = () => {
    setAnchorEl(null); // Chiude il menu
  };

  const handleLogout = async () => {
    handleMenuClose(); // Chiude il menu prima di fare logout
    await logout();
    navigate('/');
  };

  return (
    <HeaderWrapper>
      <HeaderContent>
        <Logo to={isAuthenticated ? "/dashboard" : "/"}>pray4all</Logo>
        {isMobile ? (
          <>
            <MenuButton onClick={handleDrawerToggle}>
              <MenuIcon />
            </MenuButton>
            <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerToggle}>
              <List>
                {/* Theme & Language Toggles */}
                <ListItem>
                  <ThemeToggle />
                  <ListItemText primary="Dark Mode" sx={{ ml: 1 }} />
                </ListItem>
                <ListItem>
                  <LanguageSwitcher />
                  <ListItemText primary="Language" sx={{ ml: 1 }} />
                </ListItem>

                {isAuthenticated ? (
                  <>
                    <ListItem button component={Link} to="/dashboard" onClick={handleDrawerToggle}>
                      <ListItemText primary="Dashboard" />
                    </ListItem>

                    <ListItem button component={Link} to="/circles" onClick={handleDrawerToggle}>
                      <ListItemIcon>
                        <GroupIcon />
                      </ListItemIcon>
                      <ListItemText primary="Prayer Circles" />
                    </ListItem>

                    <ListItem button component={Link} to="/messages" onClick={handleDrawerToggle}>
                      <ListItemIcon>
                        <Badge badgeContent={messages} color="error">
                          <MailIcon />
                        </Badge>
                      </ListItemIcon>
                      <ListItemText primary="Messages" />
                    </ListItem>

                    <ListItem button component={Link} to="/search" onClick={handleDrawerToggle}>
                      <ListItemIcon>
                        <SearchIcon />
                      </ListItemIcon>
                      <ListItemText primary="Search Prayers" />
                    </ListItem>

                    {userData?.is_admin && (
                      <ListItem button component={Link} to="/admin" onClick={handleDrawerToggle}>
                        <ListItemIcon>
                          <AdminPanelSettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Admin Panel" />
                      </ListItem>
                    )}

                    <ListItem button component={Link} to="/profile" onClick={handleDrawerToggle}>
                      <ListItemText primary="My Profile" />
                    </ListItem>
                    <ListItem button onClick={() => {
                      handleLogout();
                      handleDrawerToggle();
                    }}>
                      <ListItemText primary="Logout" />
                    </ListItem>
                  </>
                ) : (
                  <ListItem button component={Link} to="/login" onClick={handleDrawerToggle}>
                    <ListItemText primary="Login" />
                  </ListItem>
                )}
              </List>
            </Drawer>
          </>
        ) : (
          <NavLinks>
            {/* Theme & Language Toggles for Desktop */}
            <ThemeToggle color="inherit" />
            <LanguageSwitcher color="inherit" sx={{ mr: 2 }} />

            {isAuthenticated ? (
              <>
                <NavLink to="/dashboard">Dashboard</NavLink>
                <NavLink to="/circles">Prayer Circles</NavLink>
                <NavLink to="/messages">Messages</NavLink>
                <NavLink to="/search">Search</NavLink>
                {userData?.is_admin && (
                  <NavLink to="/admin">Admin</NavLink>
                )}

<UserMenu onClick={handleMenuOpen}>
  <Avatar
    src={userData?.profile_picture_url || ''}
    alt={userData?.username}
    sx={{ bgcolor: "#ffffff", color: "#1b58a0" }}
  >
    {!userData?.profile_picture_url && userData?.username?.[0].toUpperCase()}
  </Avatar>
  <ArrowDropDownIcon style={{ color: '#ffffff' }} />
</UserMenu>
<Menu
  anchorEl={anchorEl}
  open={Boolean(anchorEl)}
  onClose={handleMenuClose}
  anchorOrigin={{
    vertical: 'bottom',
    horizontal: 'right',
  }}
  transformOrigin={{
    vertical: 'top',
    horizontal: 'right',
  }}
>
					  <StyledMenuItem component={Link} to="/profile" onClick={handleMenuClose}>
						<ListItemIcon>
						  <Avatar sx={{ width: 24, height: 24 }} /> {/* Avatar piccolo per il profilo */}
						</ListItemIcon>
						My Profile
					  </StyledMenuItem>
					  <StyledMenuItem>
						<ListItemIcon>
						  <Badge badgeContent={notifications} color="error">
							<NotificationsIcon />
						  </Badge>
						</ListItemIcon>
						Notifiche
					  </StyledMenuItem>
					  <StyledMenuItem>
						<ListItemIcon>
						  <Badge badgeContent={messages} color="error">
							<MailIcon />
						  </Badge>
						</ListItemIcon>
						Messaggi
					  </StyledMenuItem>
					  <StyledMenuItem onClick={handleLogout}>
						<ListItemIcon>
						  <ExitToAppIcon />
						</ListItemIcon>
						Logout
					  </StyledMenuItem>
					</Menu>
              </>
            ) : (
              <NavLink to="/login">Login</NavLink>
            )}
          </NavLinks>
        )}
      </HeaderContent>
    </HeaderWrapper>
  );
}

export default Header;
